/**
 * Estimated monthly usage pricing (AUD) — calculator rules for marketing /pricing page.
 *
 * Platform fee (AUD / month) scales **down** as usage (calls + SMS + Facebook charges) grows:
 * - usage &lt; $100 → $30
 * - $100 ≤ usage &lt; $200 → $20
 * - $200 ≤ usage &lt; $300 → $10
 * - usage ≥ $300 → $0
 */

const CALL_AUD = 0.5;
const SMS_AUD = 0.1;
/** Facebook tiers (interactions / month): 0–100 free; 101–200 flat $10; each 100 above 200 up to 500 adds $30. */
const FACEBOOK_FREE_CAP = 100;
const FACEBOOK_SECOND_TIER_FLAT_AUD = 10;
const FACEBOOK_SECOND_TIER_CAP = 200;
const FACEBOOK_ABOVE_200_BLOCK_AUD = 30;
const FACEBOOK_SLIDER_MAX = 500;

/** Usage subtotal = calls + SMS + Facebook line items (excludes platform). */
export function platformFeeFromUsageSubtotalAUD(usageSubtotalAUD) {
  const u = Math.max(0, Number(usageSubtotalAUD) || 0);
  if (u < 100) return 30;
  if (u < 200) return 20;
  if (u < 300) return 10;
  return 0;
}

/**
 * @param {{ calls: number; sms: number; facebookInteractions: number }} usage
 * @returns {{
 *   platformAUD: number;
 *   callsAUD: number;
 *   smsAUD: number;
 *   facebookAUD: number;
 *   usageSubtotalAUD: number;
 *   totalMonthlyAUD: number;
 *   facebookFirstTierFlat: boolean;
 *   facebookOverageCount: number;
 * }}
 */
export function estimateMonthlyUsageAUD({ calls = 0, sms = 0, facebookInteractions = 0 }) {
  const c = Math.max(0, Math.floor(Number(calls)) || 0);
  const s = Math.max(0, Math.floor(Number(sms)) || 0);
  const fb = Math.max(
    0,
    Math.min(FACEBOOK_SLIDER_MAX, Math.floor(Number(facebookInteractions)) || 0)
  );

  /** 1–100 free; 101–200 flat $10; 201–500: +$30 per 100-interaction block above 200. */
  let facebookAUD = 0;
  let facebookFirstTierFlat = false;
  let facebookOverageCount = 0;
  if (fb <= FACEBOOK_FREE_CAP) {
    facebookAUD = 0;
  } else if (fb <= FACEBOOK_SECOND_TIER_CAP) {
    facebookAUD = FACEBOOK_SECOND_TIER_FLAT_AUD;
    facebookFirstTierFlat = true;
  } else {
    const above200 = fb - FACEBOOK_SECOND_TIER_CAP;
    const blocksAbove200 = Math.ceil(above200 / 100);
    facebookOverageCount = above200;
    facebookAUD =
      FACEBOOK_SECOND_TIER_FLAT_AUD + blocksAbove200 * FACEBOOK_ABOVE_200_BLOCK_AUD;
  }

  const callsAUD = c * CALL_AUD;
  const smsAUD = s * SMS_AUD;
  const usageSubtotalAUD = callsAUD + smsAUD + facebookAUD;
  const platformAUD = platformFeeFromUsageSubtotalAUD(usageSubtotalAUD);
  const totalMonthlyAUD = platformAUD + usageSubtotalAUD;

  return {
    platformAUD,
    callsAUD,
    smsAUD,
    facebookAUD,
    usageSubtotalAUD,
    totalMonthlyAUD,
    facebookFirstTierFlat,
    facebookOverageCount,
    rates: {
      callAUD: CALL_AUD,
      smsAUD: SMS_AUD,
      facebookFreeCap: FACEBOOK_FREE_CAP,
      facebookSecondTierFlatAUD: FACEBOOK_SECOND_TIER_FLAT_AUD,
      facebookSecondTierCap: FACEBOOK_SECOND_TIER_CAP,
      facebookAbove200BlockAUD: FACEBOOK_ABOVE_200_BLOCK_AUD,
      facebookSliderMax: FACEBOOK_SLIDER_MAX,
    },
  };
}
